import { AxiosInstance } from 'axios'
import { useMutation, useQueryClient } from 'react-query'

import { TEAMS_BASE_QUERY_KEY, Team } from '@dc-shared-web/hooks/api/team'
import { useToast } from '@dc-shared-web/components/shadcn/use-toast'
import { useAxiosContext } from '@dc-shared-web/providers/AxiosProvider'

type CreateTeamParams = {
  name: string
  customerId: string
  internal: boolean
  entitlements: {
    [key in
      | 'sso'
      | 'word'
      | 'crunch assist'
      | 'procore'
      | 'cheat sheet'
      | 'high_security'
      | 'project_features'
      | 'project_editor']: boolean
  }
}

type CreateTeamResponse = {
  uuid: string
  name: string
}

const createTeam = async (
  apiClient: AxiosInstance,
  team: CreateTeamParams
): Promise<CreateTeamResponse> => {
  const { data } = await apiClient.post(`/api/teams`, team)
  return data
}

export const useCreateTeam = (params?: {
  onSuccess?: () => void
  onError?: () => void
}) => {
  const apiClient = useAxiosContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(
    (mutationParams: CreateTeamParams) => createTeam(apiClient, mutationParams),
    {
      onSuccess: async (_, successParams) => {
        await queryClient.invalidateQueries<Team[]>(
          `${TEAMS_BASE_QUERY_KEY}Details`,
          { refetchActive: true, refetchInactive: true }
        )

        toast({
          variant: 'success',
          description: `${successParams.name} has been added to Document Crunch.`,
        })

        params?.onSuccess?.()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Error creating team',
          description: 'If the issue persists, please contact support.',
        })
      },
    }
  )
}
